import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environment/environment';
import { LoginConstants, LoginResponse, User, UserRoles } from '../constants/constants';
import { Route, Router } from '@angular/router';
import { DataService } from './data.service';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { AuthService } from './auth.service';
import { SwPush } from '@angular/service-worker';

interface PushMessage {
  notification?: {
    title?: string;
    body?: string;
    icon?: string;
    badge?: string;
    tag?: string;
    data?: any;
    vibrate?: number[];
  };
  title?: string;
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    private http: HttpClient,
    private router: Router,
    private dataService: DataService,
    private authService: AuthService,
    private swPush: SwPush
  ) {
    // Debug service worker registration
    navigator.serviceWorker.getRegistrations().then(registrations => {
      console.log('Active service workers:', registrations.map(reg => ({
        scope: reg.scope,
        active: reg.active?.scriptURL
      })));
    });

    // Set up push notification handling
    if (this.swPush.isEnabled) {
      this.swPush.messages.subscribe((message: PushMessage) => {
        console.log('Received push message:', message);

        const title = message.notification?.title || message.title || 'Bell Notification';
        const options = {
          body: message.notification?.body || message.body,
          icon: message.notification?.icon || message.icon || '/bell-320x320.png',
          badge: message.notification?.badge || '/bell-320x320.png',
          tag: message.notification?.tag || message.tag || 'bell-tag',
          data: message.notification?.data || message,
          requireInteraction: true,
          actions: [
            {
              action: 'open',
              title: 'Open App'
            }
          ]
        };

        // Use the Notifications API directly
        if (Notification.permission === 'granted') {
          new Notification(title, options);
        }
      });

      this.swPush.notificationClicks.subscribe(({ action, notification }) => {
        console.log('Notification clicked:', { action, notification });
        // Handle notification click - navigate to appropriate route
        if (action === 'open' || !action) {
          this.router.navigate(['/']);
        }
      });
    }
  }

  login(username: string, password: string){
    return this.http.post<LoginResponse>(environment.baseUrl+'login',{username,password});
  }

  register(user: User){
    return this.http.post(environment.baseUrl+'register',user);
  }

  updateUser(user: User){
    return this.http.post(environment.baseUrl+'updateUser',user);
  }

  deleteUser(username: string){
    let params = new HttpParams();
    params = params.append('username',username);
    return this.http.delete(environment.baseUrl+'deleteUser',{params});
  }

  refreshToken(refresh_token: string){
    return this.http.post<LoginResponse>(environment.baseUrl+'refresh', {refresh_token})
  }

  getAllUsers(){
    return this.http.get<any[]>(environment.baseUrl+'getAllUsers');
  }

  getUserRoleIfTokenValid(): Observable<string>{
    const token = this.dataService.getAccessToken();
    if(environment.isController){
      return of(UserRoles.EDITOR);
    }
    if (!token) return of(null);

    // Decode and check expiration (assuming JWT)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    if(payload.exp > currentTime){
      const resp:string =  (payload.userrole || UserRoles.VIEWER)
      return of(resp);
    } else {
      return this.refreshToken(this.dataService.getRefreshToken()).pipe(switchMap(loginResp=>{
        this.saveLogin(loginResp);
        const token = loginResp.access_token;
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        if(payload.exp > currentTime){
          const resp:string =  (payload.userrole || UserRoles.VIEWER)
          return of(resp);
        }
        return of(null);
      }), catchError(err=>of(null)));
    }
  }

  logout(){
    this.authService.clearTokens();
    this.router.navigate(['login']);
  }

  saveLogin(loginRes: LoginResponse) {
    this.authService.setTokens(loginRes.access_token, loginRes.refresh_token);
  }

  manualLogin(resp: LoginResponse){
    this.saveLogin(resp);
    this.getUserRoleIfTokenValid().subscribe(role=>{
      if(role == UserRoles.SUPER_ADMIN){
        this.router.navigate(["user-management"]);
      } else {
        this.router.navigate(["/"]);
      }
    })
    this.checkNotificationPermission();
  }

  checkNotificationPermission() {
    console.log('Checking notification permission');
    console.log('SwPush enabled:', this.swPush.isEnabled);

    if (!this.swPush.isEnabled) {
      console.log('Push notifications are not enabled or supported');
      return;
    }

    Notification.requestPermission().then(permission => {
      console.log('Notification permission:', permission);
      if (permission === 'granted') {
        this.swPush.subscription.subscribe(subscription => {
          console.log('Current push subscription:', subscription);
          if (!subscription) {
            console.log("Subscription not yet found. creating one");
            this.subscribeToPush();
          } else {
            console.log("Subscription already exists");
          }
        });
      } else {
        console.log('Permission denied or SwPush not enabled');
      }
    });
  }

  subscribeToPush() {
    console.log('Attempting to subscribe to push notifications');
    console.log('VAPID key:', environment.vapidPubKey);

    this.swPush.requestSubscription({
      serverPublicKey: environment.vapidPubKey
    })
    .then(sub => {
      console.log('Push subscription created:', sub);
      this.pushNotificationSubscription(sub).subscribe(() => {
        console.log('Push subscription saved to server!');
      });
    })
    .catch(err => {
      console.error('Push subscription failed:', err);
    });
  }

  pushNotificationSubscription(sub: any){
    return this.http.post(environment.baseUrl+'push-subscriptions', sub);
  }

  publishToBell(topic: string, payload: string){
    return this.http.post(environment.baseUrl+"publish", {topic, payload})
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.http.post(environment.baseUrl + 'changePassword', { currentPassword, newPassword });
  }

  getActionLogs(page: number, pageSize: number, church?: string) {
    let params = new HttpParams()
      .set('page', page)
      .set('per_page', pageSize);

    if (church) {
      params = params.set('church', church);
    }

    return this.http.get<{items: any[], total: number, page: number, per_page: number, pages: number}>(environment.baseUrl + 'getActionLogs', { params });
  }

}
